import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DIALOG_TYPE } from '../../../../../../constants';
import {
  CalendarClockIcons,
  CircleCheckIcons,
  LinkIcons,
  MegaPhoneIcons,
} from '../../../../../../icons/icons';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../i18n/constants/keys';
import {
  Button,
  ContextMenu,
  DataTable,
  Dialog,
  FilterSearch,
  FloatingLabelInput,
  MainLayout,
} from '../../../../../../ui';
import { TrashIcon } from '../../../../../../ui/icons/icons';
import {
  getInterviewRound,
  interviewRoundResults,
} from '../../../../../../utils/api_service/SmartInterview';
import { ICandidate } from '../../../../../../utils/types/JobOpenings';
import { IInterviewResult } from '../Results/Hooks';
import { RecordInterviewDialog } from './Components';
import { InviteCandidates } from './Containers';
import { PanelistNotesDialog } from './Containers/Notes';
import { getColumns } from './Functions';
import { useCandidates } from './Hooks';

interface ICandidatesProps {
  interviewRoundID: string;
  jobOpeningId: string;
  allRounds: Array<{
    id: string;
    title: string;
  }>;
  currentRoundIndex: number;
}

export const Candidates = ({
  interviewRoundID,
  allRounds,
  currentRoundIndex,
}: ICandidatesProps) => {
  const { t } = useTranslation(NS.adminPages);
  const SI = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews;
  const {
    handleSearch,
    handleApplyFilters,
    onPageChange,
    totalPages,
    pageNumber,
    interviewApplicants,
    isLoading,
    totalRecords,
    handleRecordingSave,
    openInvite,
    openMenu,
    closeMenu,
    anchorEl,
    isOpenInvite,
    closeInvite,
    onInvitesSent,
    selectedRow,
    menuContainerRef,
    makeMenuItemsFor,
    isDeleteOpen,
    handleConfirmDelete,
    setDeleteOpen,
    setSelectedRow,
    isUpdateOpen,
    handleApplyUpdate,
    setUpdateOpen,
    isVirtualType,
    meetingLink,
    setMeetingLink,
    isConfirmOpen,
    inPersonAddress,
    setInPersonAddress,
    handleConfirmInterview,
    generateMeetingLink,
    setGenerateMeetingLink,
    hostCalendar,
    askHostToConnectCalendar,
    isRequestingHostCalendar,
    hostCalendarRequested,
    setConfirmOpen,
    isPanelistNotesOpen,
    handleClosePanelistNotes,
    onOpenPanelistNotes,
    recordingRowId,
    openRecordClick,
    closeRecordClick,
    isRecordingDialogOpen,
  } = useCandidates({ interviewRoundID });
  const [isNotFirstRound, setIsNotFirstRound] = useState(false);

  // Fetch round details when component mounts or interviewRoundID changes
  useEffect(() => {
    const fetchRoundDetails = async () => {
      if (!interviewRoundID) return;

      try {
        const resp = await getInterviewRound(interviewRoundID);
        if (!resp.ok) {
          throw new Error(`Failed to fetch round details: ${resp.statusText}`);
        }

        const data = await resp.json();
        // Check if it's not first round based on title
        const isNotFirst =
          data.title?.toLowerCase().includes('round') &&
          !data.title?.toLowerCase().includes('round 1');
        setIsNotFirstRound(isNotFirst);
      } catch {
        setIsNotFirstRound(false);
      }
    };

    fetchRoundDetails();
  }, [interviewRoundID]);

  // Table & Search

  // Invite Drawer
  const [results, setResults] = useState<IInterviewResult[]>([]);

  // Fetch results when interviewRoundID changes
  React.useEffect(() => {
    const fetchResults = async () => {
      if (!interviewRoundID || currentRoundIndex < 0) return;

      try {
        if (currentRoundIndex > 0) {
          // Get results from the previous round
          const previousRound = allRounds[currentRoundIndex - 1];
          const resp = await interviewRoundResults(previousRound.id);

          if (!resp.ok) {
            throw new Error(`Failed to fetch results: ${resp.statusText}`);
          }

          const data = await resp.json();
          // Include all results from the previous round
          setResults(data.results || []);
        } else {
          // For first round, we don't need previous results
          setResults([]);
        }
      } catch {
        setResults([]);
      }
    };

    if (interviewRoundID) {
      fetchResults();
    }
  }, [interviewRoundID, currentRoundIndex, allRounds]);

  return (
    <>
      <MainLayout>
        <div className='mb-3 flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between'>
          <div className='min-w-0 flex-1'>
            <FilterSearch
                onSearch={handleSearch}
                onApplyFilters={handleApplyFilters}
            />
          </div>
          <div className='flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between xl:justify-end'>
            <div className='flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1.5 sm:mr-2'>
              { [
                {
                  color: 'bg-gradient-to-r from-indigo-500 to-indigo-600',
                  label: t(SI.candidates.status.invited),
                },
                {
                  color: 'bg-gradient-to-r from-violet-500 to-violet-600',
                  label: t(SI.candidates.status.scheduled),
                },
                {
                  color: 'bg-gradient-to-r from-blue-500 to-blue-600',
                  label: t(SI.candidates.status.confirmed),
                },
                {
                  color: 'bg-gradient-to-r from-emerald-500 to-emerald-600',
                  label: t(SI.candidates.status.live),
                },
                {
                  color: 'bg-gradient-to-r from-green-500 to-green-600',
                  label: t(SI.candidates.status.finished),
                },
              ].map((item) => (
                <div key={item.label} className='flex items-center gap-1.5'>
                  <div className={`h-2 w-2 rounded ${item.color} shadow-sm`} />
                  <span className='text-[11px] font-medium text-gray-600 sm:text-xs'>
                    { item.label }
                  </span>
                </div>
              )) }
            </div>
            <Button
                label={t(SI.candidates.inviteButton)}
                variant='primary'
                onClick={openInvite}
                className='min-h-9 w-full px-3 py-1.5 text-sm sm:w-auto'
            />
          </div>
        </div>
        <DataTable
            isLoading={isLoading}
            columns={getColumns({
              handleOpenMenu: openMenu,
              handleCloseMenu: closeMenu,
              anchorEl,
              onRecordInterview: openRecordClick,
              onOpenPanelistNotes: onOpenPanelistNotes,
              recordingRowId: recordingRowId,
              t,
            })}
            data={interviewApplicants}
            rowsPerPage={10}
            currentPage={pageNumber}
            onPageChange={onPageChange}
            totalPages={totalPages}
            totalRecords={totalRecords}
        />
      </MainLayout>
      <InviteCandidates
          interviewRoundID={interviewRoundID}
          isOpen={isOpenInvite}
          onCloseDrawer={closeInvite}
          onInvitesSent={onInvitesSent}
          results={results}
          isNotFirstRound={isNotFirstRound}
      />
      { anchorEl && selectedRow && (
        <div ref={menuContainerRef}>
          <ContextMenu<ICandidate>
              anchorEl={anchorEl}
              onClose={closeMenu}
              row={selectedRow}
              menuItems={makeMenuItemsFor(selectedRow)}
          />
        </div>
      ) }
      { /* Delete Candidate */ }
      <Dialog
          isOpen={isDeleteOpen}
          title={t(SI.candidates.deleteDialog.title)}
          handleApply={() => selectedRow && handleConfirmDelete(selectedRow)}
          cancelButtonLable={t(SI.candidates.deleteDialog.cancel)}
          confirmButtonLable={t(SI.candidates.deleteDialog.confirm)}
          dialogType={DIALOG_TYPE.ERROR}
          onClose={() => {
            setDeleteOpen(false);
            setSelectedRow(null);
          }}
      >
        <div className='flex flex-col items-center space-y-4 p-4'>
          <TrashIcon className='w-12 h-12 text-red-600' />
          <p className='text-lg text-gray-800 text-center'>
            Are you sure you want to remove{ ' ' }
            <span className='font-semibold'>
              { selectedRow?.firstName } { selectedRow?.lastName }
            </span>{ ' ' }
            from this interview?
          </p>
          <p className='text-sm text-gray-500 text-center'>
            { t(SI.candidates.deleteDialog.warning) }
          </p>
        </div>
      </Dialog>
      { /* Update Interview Details */ }
      <Dialog
          isOpen={isUpdateOpen}
          title={t(SI.candidates.updateDialog.title)}
          handleApply={() => selectedRow && handleApplyUpdate(selectedRow)}
          cancelButtonLable={t(SI.candidates.updateDialog.cancel)}
          confirmButtonLable={t(SI.candidates.updateDialog.save)}
          dialogType={DIALOG_TYPE.INFO}
          onClose={() => {
            setUpdateOpen(false);
            setSelectedRow(null);
          }}
          disabled={isVirtualType && !generateMeetingLink && meetingLink.trim() === ''}
      >
        <p>{ isVirtualType ? t(SI.candidates.updateDialog.virtual) : t(SI.candidates.updateDialog.inPerson) } Interview</p>
        { isVirtualType && (
          <div className='mt-3 rounded-lg border border-gray-200 bg-gray-50 p-3'>
            <label className='flex cursor-pointer items-start gap-3'>
              <input
                  type='checkbox'
                  className='mt-0.5 h-4 w-4 accent-emerald-700'
                  checked={generateMeetingLink}
                  disabled={!hostCalendar?.connected}
                  onChange={(e) => setGenerateMeetingLink(e.target.checked)}
              />
              <span className='text-sm'>
                <span className='font-medium text-gray-900'>
                  { t(SI.candidates.updateDialog.generateLink) }
                </span>
                <span className='mt-0.5 block text-gray-500'>
                  { hostCalendar?.connected
                    ? t(SI.candidates.updateDialog.generateLinkHint, {
                      host: hostCalendar.hostName,
                    })
                    : t(SI.candidates.updateDialog.generateLinkUnavailable, {
                      host: hostCalendar?.hostName || 'The interview host',
                    }) }
                </span>
              </span>
            </label>

            { /* The recruiter cannot connect on the host's behalf, so offer
                 the two things they can do: enter a link by hand below, or
                 ask the host and be emailed once they have connected. */ }
            { !hostCalendar?.connected && (
              hostCalendarRequested ? (
                <p className='mt-3 pl-7 text-sm text-emerald-700'>
                  { t(SI.candidates.updateDialog.hostCalendarRequested) }
                </p>
              ) : (
                <button
                    type='button'
                    disabled={isRequestingHostCalendar}
                    onClick={() => selectedRow && askHostToConnectCalendar(selectedRow)}
                    className='mt-3 ml-7 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:opacity-50'
                >
                  { t(SI.candidates.updateDialog.askHostToConnect) }
                </button>
              )
            ) }
          </div>
        ) }
        { isVirtualType && !generateMeetingLink ? (
          <FloatingLabelInput
              label={t(SI.candidates.updateDialog.meetingLink)}
              value={meetingLink}
              onChange={(e) => setMeetingLink(e.target.value)}
              placeholder='https://zoom.us/…'
          />
        ) : !isVirtualType ? (
          <FloatingLabelInput
              label={t(SI.candidates.updateDialog.physicalAddress)}
              value={inPersonAddress}
              onChange={(e) => setInPersonAddress(e.target.value)}
              placeholder='123 Main St, City'
          />
        ) : null }
      </Dialog>
      { /* Confirm Interview */ }

      <Dialog
          isOpen={isConfirmOpen}
          title={t(SI.candidates.confirmDialog.title)}
          handleApply={() => selectedRow && handleConfirmInterview(selectedRow)}
          cancelButtonLable={t(SI.candidates.confirmDialog.cancel)}
          confirmButtonLable={t(SI.candidates.confirmDialog.confirm)}
          dialogType={DIALOG_TYPE.INFO}
          onClose={() => {
            setConfirmOpen(false);
            setSelectedRow(null);
          }}
      >
        { selectedRow && (
          <div className='space-y-6 p-4'>
            { /* Date / Time */ }
            <div className='flex items-center gap-3 bg-gray-50 p-3 rounded-md'>
              <CalendarClockIcons className='w-6 h-6 text-emerald-700' />
              <div>
                <div className='text-sm text-gray-500'>{ t(SI.candidates.confirmDialog.when) }</div>
                <div className='text-lg font-semibold text-gray-900'>
                  { new Date(
                    Number(selectedRow.interviewStartTime) * 1000,
                  ).toLocaleString([], {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  }) }
                </div>
              </div>
            </div>

            { /* Type */ }
            <div className='flex items-center gap-3 bg-gray-50 p-3 rounded-md'>
              <CircleCheckIcons className='w-6 h-6 text-green-500' />
              <div>
                <div className='text-sm text-gray-500'>{ t(SI.candidates.confirmDialog.type) }</div>
                <div className='text-md font-medium text-gray-900'>
                  { selectedRow.interviewType }
                </div>
              </div>
            </div>

            { /* Link or Address */ }
            <div className='flex items-center gap-3 bg-gray-50 p-3 rounded-md'>
              { selectedRow.interviewType === 'Virtual' ? (
                <LinkIcons className='w-6 h-6 text-blue-500' />
              ) : (
                <MegaPhoneIcons className='w-6 h-6 text-red-500' />
              ) }
              <div className='flex-1'>
                <div className='text-sm text-gray-500'>
                  { selectedRow.interviewType === 'Virtual'
                    ? t(SI.candidates.confirmDialog.meetingLink)
                    : t(SI.candidates.confirmDialog.location) }
                </div>
                { selectedRow.interviewType === 'Virtual' ? (
                  <a
                      href={selectedRow.meetingLink}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='text-indigo-600 underline break-all'
                  >
                    { selectedRow.meetingLink }
                  </a>
                ) : (
                  <div className='text-gray-900'>
                    { selectedRow.inPersonAddress }
                  </div>
                ) }
              </div>
            </div>

            { /* Footer note */ }
            <div className='flex items-center gap-2 text-sm text-gray-700'>
              <CircleCheckIcons className='w-5 h-5 text-green-500' />
              <span>{ t(SI.candidates.confirmDialog.confirmNote) }</span>
            </div>
          </div>
        ) }
      </Dialog>

      <RecordInterviewDialog
          handleCloseRecordDialog={closeRecordClick}
          open={isRecordingDialogOpen}
          onRecordInterview={handleRecordingSave}
          onOpenPanelistNotes={onOpenPanelistNotes}
          candidate={selectedRow ?? ({} as ICandidate)}
      />
      <PanelistNotesDialog
          open={isPanelistNotesOpen}
          handleClosePanelistNotes={handleClosePanelistNotes}
          interviewRoundId={selectedRow?.interviewRoundId ?? ''}
          applicantId={selectedRow?.id ?? ''}
          email={selectedRow?.email ?? ''}
          intervieweeName={`${selectedRow?.firstName ?? ''} ${
            selectedRow?.lastName ?? ''
          }`}
          onSuccess={() => {
          // Optional: refetch notes, show toast, etc.
          }}
      />
    </>
  );
};
